import React, { createContext, useContext, useState, useCallback } from "react";

export interface Widget {
  id: string;
  instanceId?: string; // Optional unique ID per instance
  type: string;
  title: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isPinned?: boolean; // Optional: Indicates if the widget is pinned
  isTeamWidget?: boolean; // Optional: Indicates if it's a team-level widget
  [key: string]: any; // Restore index signature for flexibility
}

interface WidgetContextType {
  widgets: Widget[];
  setWidgets: React.Dispatch<React.SetStateAction<Widget[]>>;
  addWidget: (widget: Omit<Widget, "id">) => void;
  removeWidget: (id: string) => void;
  updateWidget: (id: string, updates: Partial<Omit<Widget, "id">>) => void;
  clearWidgets: () => void;
}

const WidgetContext = createContext<WidgetContextType | undefined>(undefined);

const DEFAULT_WIDGETS: Omit<Widget, "id">[] = [
  {
    type: "notes",
    title: "Snelle Notities",
    position: { x: 0, y: 0 },
    size: { width: 2, height: 2 },
  },
  {
    type: "rss",
    title: "RSS Feed",
    position: { x: 2, y: 0 },
    size: { width: 1, height: 2 },
    feeds: ["https://tweakers.net/feeds/nieuws.xml"],
    limit: 5
  },
  {
    type: "news",
    title: "Bedrijfsnieuws",
    position: { x: 0, y: 2 },
    size: { width: 2, height: 2 },
  },
];

export const WidgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [widgets, setWidgets] = useState<Widget[]>([]);

  const addWidget = useCallback((widgetData: Omit<Widget, "id">) => {
    const newWidget = {
      ...widgetData,
      id: widgetData.instanceId || `${widgetData.type}_${Math.random().toString(36).substr(2, 9)}`,
    } as Widget;
    setWidgets((prev) => [...prev, newWidget]);
  }, []);

  const removeWidget = useCallback((id: string) => {
    setWidgets((prev) => prev.filter((widget) => widget.id !== id));
  }, []);

  const updateWidget = useCallback((id: string, updates: Partial<Omit<Widget, "id">>) => {
    setWidgets((prev) =>
      prev.map((widget) =>
        widget.id === id ? { ...widget, ...updates } : widget
      )
    );
  }, []);

  const clearWidgets = useCallback(() => {
    setWidgets([]);
  }, []);

  return (
    <WidgetContext.Provider value={{ widgets, setWidgets, addWidget, removeWidget, updateWidget, clearWidgets }}>
      {children}
    </WidgetContext.Provider>
  );
};

export const useWidgets = () => {
  const context = useContext(WidgetContext);
  if (context === undefined) {
    throw new Error("useWidgets must be used within a WidgetProvider");
  }
  return context;
};